// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import React from 'react'
import { supabase } from '../../supabase'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      reportLogged: false,
      reportLogging: false,
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  async componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo, reportLogging: true })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id || null
      const userEmail = session?.user?.email || null
      let reporterName = 'Unknown'
      if (userId) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userId)
          .maybeSingle()
        if (profile) {
          reporterName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || userEmail
        } else {
          reporterName = userEmail
        }
      }
      await supabase.from('bug_reports').insert({
        user_id: userId,
        reporter_name: reporterName,
        category: 'React Crash',
        description: `${error.toString()}\n\nComponent stack:\n${errorInfo.componentStack}`,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        status: 'new',
        is_public: false,
      })
      this.setState({ reportLogged: true, reportLogging: false })
    } catch (logError) {
      console.error('Failed to log error to bug_reports:', logError)
      this.setState({ reportLogging: false })
    }
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    window.location.href = '/'
  }

  handleSignOut = async () => {
    await supabase.auth.signOut()
    window.location.href = '/'
  }

  handleEmailKenneth = () => {
    const subject = encodeURIComponent('Casavio — something went wrong')
    const body = encodeURIComponent(
      `Hi Kenneth,\n\nI ran into an error in Casavio.\n\nPage: ${window.location.href}\nError: ${this.state.error?.toString() || 'Unknown'}\n\nWhat I was trying to do:\n\n`
    )
    window.location.href = `mailto:kennethkrummes@gmail.com?subject=${subject}&body=${body}`
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.iconWrap}><span style={styles.icon}>🏠</span></div>
          <h1 style={styles.heading}>Something went wrong on this page</h1>
          <p style={styles.subhead}>
            Don't worry — your data is safe. We've automatically logged what happened
            and we're on it. In the meantime, here's how to keep going:
          </p>
          <div style={styles.buttonStack}>
            <button onClick={this.handleGoHome} style={styles.primaryBtn}>Go to Dashboard</button>
            <button onClick={this.handleReload} style={styles.secondaryBtn}>Try this page again</button>
            <button onClick={this.handleSignOut} style={styles.secondaryBtn}>Sign out and sign back in</button>
            <button onClick={this.handleEmailKenneth} style={styles.tertiaryBtn}>Email Kenneth directly</button>
          </div>
          <div style={styles.statusRow}>
            {this.state.reportLogging && <span style={styles.statusMuted}>Logging the error…</span>}
            {this.state.reportLogged && <span style={styles.statusOk}>✓ Error report logged automatically</span>}
          </div>
          {this.state.error && (
            <details style={styles.details}>
              <summary style={styles.summary}>Technical details (for Kenneth)</summary>
              <pre style={styles.pre}>{this.state.error.toString()}{this.state.errorInfo?.componentStack}</pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}

const styles = {
  page: { minHeight: '100vh', background: '#F2EDE4', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Calibri, sans-serif' },
  card: { background: '#FDFAF6', borderRadius: 16, padding: 40, maxWidth: 520, width: '100%', boxShadow: '0 8px 32px rgba(44, 29, 20, 0.08)', border: '1px solid rgba(154, 123, 104, 0.15)' },
  iconWrap: { textAlign: 'center', marginBottom: 16 },
  icon: { fontSize: 48, display: 'inline-block' },
  heading: { fontFamily: 'Georgia, serif', fontSize: 26, color: '#2C1D14', margin: '0 0 12px 0', textAlign: 'center', lineHeight: 1.3 },
  subhead: { fontSize: 15, color: '#9A7B68', lineHeight: 1.5, textAlign: 'center', margin: '0 0 28px 0' },
  buttonStack: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 },
  primaryBtn: { background: '#B8721F', color: '#FDFAF6', border: 'none', borderRadius: 8, padding: '14px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'Calibri, sans-serif' },
  secondaryBtn: { background: '#FDFAF6', color: '#2C1D14', border: '1px solid #9A7B68', borderRadius: 8, padding: '12px 20px', fontSize: 14, cursor: 'pointer', fontFamily: 'Calibri, sans-serif' },
  tertiaryBtn: { background: 'transparent', color: '#9A7B68', border: 'none', padding: '8px 20px', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'Calibri, sans-serif' },
  statusRow: { textAlign: 'center', minHeight: 20, marginBottom: 12 },
  statusMuted: { fontSize: 13, color: '#9A7B68', fontStyle: 'italic' },
  statusOk: { fontSize: 13, color: '#5C7A4F' },
  details: { marginTop: 16, fontSize: 12, color: '#9A7B68' },
  summary: { cursor: 'pointer', padding: '6px 0', userSelect: 'none' },
  pre: { background: '#F2EDE4', padding: 12, borderRadius: 6, fontSize: 11, overflow: 'auto', maxHeight: 200, marginTop: 8, color: '#2C1D14', fontFamily: 'Consolas, Monaco, monospace', whiteSpace: 'pre-wrap' },
}

export default ErrorBoundary
